<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { useMessage } from 'wot-design-uni'
import { getCurrentPath } from '@/utils/common'
import { useGlobalDialog } from '@/hooks/useGlobalDialog'

/**
 * 全局弹窗的渲染壳：store → wot `MessageBox` 的桥。
 *
 * 为什么需要这层桥：`useMessage()` 靠 `inject` 拿选项 ref，只能在组件 setup 里调用，
 * `http.ts` 这类纯工具模块拿不到，所以网络层只跟 store 打交道，由本组件在 setup 里调用 MessageBox。
 *
 * 自带 `selector`：选项 ref 由本组件的 `useMessage('globalDialog')` 提供，模板里的
 * `<wd-message-box selector="globalDialog" />` 消费同一个 ref，不占用布局里那套默认实例。
 */
const { dialogOptions } = storeToRefs(useGlobalDialog())
const { close } = useGlobalDialog()

const message = useMessage('globalDialog')

/** 布局是每页实例，记住当前页，避免弹窗串到别的页面 */
const currentPath = getCurrentPath()

let opening = false

watch(
  () => dialogOptions.value,
  async (option) => {
    if (!option || option.currentPage !== currentPath || opening) return
    opening = true
    try {
      const result = await message[option.showCancel === false ? 'alert' : 'confirm']({
        title: option.title || '提示',
        msg: option.content || '',
        confirmButtonText: option.confirmText || '确定',
        cancelButtonText: option.cancelText || '取消',
        showCancelButton: option.showCancel !== false,
        // 与既有交互一致：点遮罩不关闭，必须选取消或确定
        closeOnClickModal: false,
      })
      if (result.action === 'confirm') {
        option.onConfirm?.()
      } else {
        option.onCancel?.()
      }
    } catch {
      option.onCancel?.()
    } finally {
      opening = false
      close()
    }
  },
)
</script>

<template>
  <wd-message-box selector="globalDialog" />
</template>
